import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiCreditCard, FiX, FiSave, FiSearch, FiStar, FiMoreVertical, FiBookOpen, FiArrowDown, FiArrowUp, FiCheckCircle } from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy } from 'firebase/firestore';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { createLinkedAccount, ensureParentAccountExists, deleteLinkedAccount } from '../../utils/coaInitializer';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useCurrency from '../../hooks/useCurrency';
import { MasterHeaderActions, MasterAddButton, ContentCard, StatsRow, StatCard, StatIcon, StatInfo, StatValue, StatLabel } from '../../components/SharedStyles';
import useEscapeClose from '../../hooks/useEscapeClose';

const Banks = () => {
  const { activeCompany, currentUser } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'banks');
  const { formatCurrency } = useCurrency();
  const [banks, setBanks] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingBank, setEditingBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Actions menu (portal)
  const navigate = useNavigate();
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const closeActionMenu = () => setOpenActionMenuId(null);
  const toggleActionMenu = (bankId, e) => {
    if (openActionMenuId === bankId) { closeActionMenu(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);
    setActionMenuPos({ top: rect.bottom + 4, left: Math.max(8, left) });
    setOpenActionMenuId(bankId);
  };
  useEffect(() => {
    if (!openActionMenuId) return;
    const close = (e) => {
      if (!e.target.closest('[data-action-menu]') && !e.target.closest('[data-action-trigger]')) closeActionMenu();
    };
    const onScroll = () => closeActionMenu();
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [openActionMenuId]);

  const handleOpenLedger = (bank) => {
    closeActionMenu();
    if (!bank.linkedAccountId) { alert('No linked account for this bank.'); return; }
    navigate('/reports/ledger', { state: { filterAccountId: bank.linkedAccountId } });
  };
  const handleOpenBankReceipt = (bank) => {
    closeActionMenu();
    navigate('/receipts/bank-receipt', { state: { prefill: { bankAccountId: bank.linkedAccountId, bankName: bank.bankName } } });
  };
  const handleOpenBankPayment = (bank) => {
    closeActionMenu();
    navigate('/payments/bank-payment', { state: { prefill: { bankAccountId: bank.linkedAccountId, bankName: bank.bankName } } });
  };
  const [formData, setFormData] = useState({
    bankName: '',
    branchName: '',
    branchCode: '',
    accountNumber: '',
    accountTitle: '',
    accountType: 'Current',
    iban: '',
    swiftCode: '',
    isDefault: false,
    isActive: true
  });

  // Keyboard shortcuts: Ctrl+N to open add modal, Ctrl+S to submit form
  useDocumentShortcuts({
    onNew: () => { resetForm(); setShowModal(true); },
    onSave: () => { const form = document.querySelector('form'); if (form) form.requestSubmit(); },
    viewMode: showModal ? 'form' : 'list',
    saving
  });

  const loadBanks = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);
      const banksRef = collection(db, 'companies', activeCompany.id, 'banks');
      const q = query(banksRef, orderBy('bankCode', 'asc'));
      const snapshot = await getDocs(q);

      const banksList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setBanks(banksList);
      setFilteredBanks(banksList);
    } catch (error) {
      console.error('Error loading banks:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadBanks();
  }, [loadBanks]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredBanks(banks);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredBanks(banks.filter(bank =>
        bank.bankName?.toLowerCase().includes(query) ||
        bank.bankCode?.toLowerCase().includes(query) ||
        bank.branchName?.toLowerCase().includes(query) ||
        bank.accountNumber?.includes(query)
      ));
    }
  }, [searchQuery, banks]);

  const generateBankCode = async () => {
    const banksRef = collection(db, 'companies', activeCompany.id, 'banks');
    const snapshot = await getDocs(banksRef);
    const count = snapshot.size + 1;
    return `BANK-${String(count).padStart(4, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.bankName || !formData.accountNumber || !formData.accountTitle) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingBank) {
        // Update existing bank
        const bankRef = doc(db, 'companies', activeCompany.id, 'banks', editingBank.id);
        await updateDoc(bankRef, {
          ...formData,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser.uid
        });

        // If bank has linked account, update name if changed
        const oldName = `${editingBank.bankName} - ${editingBank.branchName}`;
        const newName = `${formData.bankName} - ${formData.branchName}`;
        if (editingBank.linkedAccountId && oldName !== newName) {
          try {
            const linkedAccountRef = doc(db, 'companies', activeCompany.id, 'accounts', editingBank.linkedAccountId);
            await updateDoc(linkedAccountRef, {
              name: `Bank: ${newName}`,
              updatedAt: new Date().toISOString()
            });
          } catch (accountError) {
            console.error('Error updating linked account:', accountError);
          }
        }

        // If this is set as default, unset others
        if (formData.isDefault && !editingBank.isDefault) {
          for (const bank of banks) {
            if (bank.id !== editingBank.id && bank.isDefault) {
              const otherBankRef = doc(db, 'companies', activeCompany.id, 'banks', bank.id);
              await updateDoc(otherBankRef, { isDefault: false });
            }
          }
        }
      } else {
        // First validate parent account exists (this will auto-repair COA if needed)
        const parentCheck = await ensureParentAccountExists(activeCompany.id, 'bank', currentUser.uid);

        if (!parentCheck.success) {
          alert(`Cannot create bank: ${parentCheck.message}. Please ensure Chart of Accounts is properly initialized.`);
          setSaving(false);
          return;
        }

        // Create new bank
        const banksRef = collection(db, 'companies', activeCompany.id, 'banks');
        const bankCode = await generateBankCode();

        // If this is first bank or marked as default, ensure it's the only default
        const shouldBeDefault = formData.isDefault || banks.length === 0;

        // Create bank document
        const bankDoc = await addDoc(banksRef, {
          bankCode,
          ...formData,
          isDefault: shouldBeDefault,
          currentBalance: 0,
          createdAt: new Date().toISOString(),
          createdBy: currentUser.uid
        });

        // Create linked account using new simplified API
        try {
          const accountResult = await createLinkedAccount(
            activeCompany.id,
            'bank',
            bankDoc.id,
            `${formData.bankName} - ${formData.branchName}`,
            currentUser.uid
          );

          if (accountResult.success) {
            // Update bank with linked account ID
            await updateDoc(doc(banksRef, bankDoc.id), {
              linkedAccountId: accountResult.accountId
            });
          } else {
            console.warn('Failed to create linked account:', accountResult.message);
            alert('Bank created but failed to create linked account. You can create it manually later.');
          }
        } catch (accountError) {
          console.error('Error creating linked account:', accountError);
          alert('Bank created but failed to create linked account. You may need to create it manually.');
        }

        // Unset other defaults if this is default
        if (shouldBeDefault) {
          for (const bank of banks) {
            if (bank.isDefault) {
              const otherBankRef = doc(db, 'companies', activeCompany.id, 'banks', bank.id);
              await updateDoc(otherBankRef, { isDefault: false });
            }
          }
        }
      }

      setShowModal(false);
      resetForm();
      await loadBanks();
    } catch (error) {
      console.error('Error saving bank:', error);
      alert('Failed to save bank');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bank) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Are you sure you want to delete "${bank.bankName} - ${bank.branchName}"?`)) {
      return;
    }

    try {
      const { hasReferences, message } = await checkEntityReferences(activeCompany.id, 'bank', bank.id, bank);
      if (hasReferences) {
        alert(message);
        return;
      }

      const bankRef = doc(db, 'companies', activeCompany.id, 'banks', bank.id);
      await deleteDoc(bankRef);
      await deleteLinkedAccount(activeCompany.id, 'bank', bank.id);
      await loadBanks();
    } catch (error) {
      console.error('Error deleting bank:', error);
      alert('Failed to delete bank');
    }
  };

  const setAsDefault = async (bank) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    try {
      // Unset all others
      for (const b of banks) {
        if (b.isDefault) {
          const bankRef = doc(db, 'companies', activeCompany.id, 'banks', b.id);
          await updateDoc(bankRef, { isDefault: false });
        }
      }
      // Set this one
      const bankRef = doc(db, 'companies', activeCompany.id, 'banks', bank.id);
      await updateDoc(bankRef, { isDefault: true });
      await loadBanks();
    } catch (error) {
      console.error('Error setting default bank:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      bankName: '',
      branchName: '',
      branchCode: '',
      accountNumber: '',
      accountTitle: '',
      accountType: 'Current',
      iban: '',
      swiftCode: '',
      isDefault: false,
      isActive: true
    });
    setEditingBank(null);
  };

  const openEditModal = (bank) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingBank(bank);
    setFormData({
      bankName: bank.bankName || '',
      branchName: bank.branchName || '',
      branchCode: bank.branchCode || '',
      accountNumber: bank.accountNumber || '',
      accountTitle: bank.accountTitle || '',
      accountType: bank.accountType || 'Current',
      iban: bank.iban || '',
      swiftCode: bank.swiftCode || '',
      isDefault: bank.isDefault || false,
      isActive: bank.isActive !== false
    });
    setShowModal(true);
  };

  return (
    <PageLayout pageTitle="Bank Accounts" breadcrumb="Master Data / Banks">
      <MasterHeaderActions>
        {canEdit && (
          <MasterAddButton onClick={() => { resetForm(); setShowModal(true); }}>
            <FiPlus /> Add Bank Account
          </MasterAddButton>
        )}
      </MasterHeaderActions>

      {(() => {
        const totalBanks = banks.length;
        const activeBanks = banks.filter(b => b.isActive !== false).length;
        return (
          <StatsRow>
            <StatCard>
              <StatIcon $color={colors.primary.main}><FiCreditCard /></StatIcon>
              <StatInfo>
                <StatValue>{totalBanks}</StatValue>
                <StatLabel>Total Banks</StatLabel>
              </StatInfo>
            </StatCard>
            <StatCard>
              <StatIcon $color={colors.success.main}><FiCheckCircle /></StatIcon>
              <StatInfo>
                <StatValue>{activeBanks}</StatValue>
                <StatLabel>Active Banks</StatLabel>
              </StatInfo>
            </StatCard>
          </StatsRow>
        );
      })()}

      <ContentCard>
        <SearchBar>
          <SearchInput>
            <FiSearch />
            <input
              type="text"
              placeholder="Search by bank name, branch, or account number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchInput>
        </SearchBar>

        {loading ? (
          <LoadingState>Loading bank accounts...</LoadingState>
        ) : filteredBanks.length === 0 ? (
          <EmptyState>
            <FiCreditCard />
            <p>{searchQuery ? 'No banks found matching your search' : 'No bank accounts added yet'}</p>
            <small>Click "Add Bank Account" to create your first bank</small>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Bank</Th>
                <Th>Account Number</Th>
                <Th>Account Title</Th>
                <Th>Type</Th>
                <Th style={{ textAlign: 'right' }}>Balance</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredBanks.map(bank => (
                <Tr key={bank.id}>
                  <Td><strong>{bank.bankCode}</strong></Td>
                  <Td>
                    <BankInfo>
                      <BankName>
                        {bank.bankName}
                        {bank.isDefault && <DefaultBadge><FiStar /> Default</DefaultBadge>}
                      </BankName>
                      <BranchName>{bank.branchName}</BranchName>
                    </BankInfo>
                  </Td>
                  <Td>{bank.accountNumber}</Td>
                  <Td>{bank.accountTitle}</Td>
                  <Td>
                    <TypeBadge $type={bank.accountType}>
                      {bank.accountType}
                    </TypeBadge>
                  </Td>
                  <Td style={{ textAlign: 'right' }}>
                    <BalanceAmount $positive={bank.currentBalance > 0}>
                      {formatCurrency(bank.currentBalance)}
                    </BalanceAmount>
                  </Td>
                  <Td>
                    <StatusBadge $active={bank.isActive}>
                      {bank.isActive ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <ActionButtons>
                      {!bank.isDefault && canEdit && (
                        <IconButton onClick={() => setAsDefault(bank)} title="Set as Default">
                          <FiStar />
                        </IconButton>
                      )}
                      <IconButton
                        data-action-trigger
                        onClick={(e) => toggleActionMenu(bank.id, e)}
                        title="Actions"
                      >
                        <FiMoreVertical />
                      </IconButton>
                    </ActionButtons>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </ContentCard>

      {/* Actions menu (portal) */}
      {openActionMenuId && (() => {
        const bank = banks.find(b => b.id === openActionMenuId);
        if (!bank) return null;
        return createPortal(
          <ActionDropdown
            data-action-menu
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
          >
            {canChange && (
              <ActionDropItem onClick={() => { closeActionMenu(); openEditModal(bank); }}>
                <FiEdit2 size={14} /> Edit
              </ActionDropItem>
            )}
            <ActionDropItem onClick={() => handleOpenLedger(bank)}>
              <FiBookOpen size={14} /> View Ledger
            </ActionDropItem>
            <ActionDropItem onClick={() => handleOpenBankReceipt(bank)}>
              <FiArrowDown size={14} /> New Bank Receipt
            </ActionDropItem>
            <ActionDropItem onClick={() => handleOpenBankPayment(bank)}>
              <FiArrowUp size={14} /> New Bank Payment
            </ActionDropItem>
            {canDelete && (
              <>
                <ActionDropDivider />
                <ActionDropItem $danger onClick={() => { closeActionMenu(); handleDelete(bank); }}>
                  <FiTrash2 size={14} /> Delete
                </ActionDropItem>
              </>
            )}
          </ActionDropdown>,
          document.body
        );
      })()}

      {/* Modal */}
      {showModal && (
        <ModalOverlay>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FiCreditCard />
                {editingBank ? 'Edit Bank Account' : 'Add Bank Account'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}><FiX /></CloseButton>
            </ModalHeader>

            <ModalBody>
              <form onSubmit={handleSubmit}>
                <SectionTitle>Bank Information</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Bank Name *</Label>
                    <Input
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      placeholder="e.g., Habib Bank Limited"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Branch Name</Label>
                    <Input
                      value={formData.branchName}
                      onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
                      placeholder="e.g., Gulberg Branch"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Branch Code</Label>
                    <Input
                      value={formData.branchCode}
                      onChange={(e) => setFormData({ ...formData, branchCode: e.target.value })}
                      placeholder="e.g., 0123"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Account Type *</Label>
                    <Select
                      value={formData.accountType}
                      onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                    >
                      <option value="Current">Current Account</option>
                      <option value="Savings">Savings Account</option>
                      <option value="Fixed Deposit">Fixed Deposit</option>
                    </Select>
                  </FormGroup>
                </FormGrid>

                <SectionTitle>Account Details</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Account Number *</Label>
                    <Input
                      value={formData.accountNumber}
                      onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                      placeholder="Bank account number"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Account Title *</Label>
                    <Input
                      value={formData.accountTitle}
                      onChange={(e) => setFormData({ ...formData, accountTitle: e.target.value })}
                      placeholder="Account holder name"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>IBAN</Label>
                    <Input
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
                      placeholder="e.g., PK36HABB0000001123456702"
                      maxLength={24}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>SWIFT Code</Label>
                    <Input
                      value={formData.swiftCode}
                      onChange={(e) => setFormData({ ...formData, swiftCode: e.target.value.toUpperCase() })}
                      placeholder="e.g., HABORPKAXXX"
                    />
                  </FormGroup>
                </FormGrid>

                <SectionTitle>Settings</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Status</Label>
                    <Select
                      value={formData.isActive ? 'active' : 'inactive'}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Default Bank</Label>
                    <CheckboxWrapper>
                      <input
                        type="checkbox"
                        checked={formData.isDefault}
                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      />
                      <span>Use as default for payments</span>
                    </CheckboxWrapper>
                  </FormGroup>
                </FormGrid>

                <ModalActions>
                  <CancelButton type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </CancelButton>
                  {canEdit && (
                    <SaveButton type="submit" disabled={saving}>
                      <FiSave />
                      {saving ? 'Saving...' : (editingBank ? 'Update Bank' : 'Create Bank')}
                    </SaveButton>
                  )}
                </ModalActions>
              </form>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// Styled Components

const SearchBar = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.border.light};
`;

const SearchInput = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  max-width: 400px;

  svg {
    position: absolute;
    left: 12px;
    color: ${colors.text.tertiary};
  }

  input {
    width: 100%;
    padding: 10px 10px 10px 40px;
    border: 1px solid ${colors.border.light};
    border-radius: ${borderRadius.md};
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: ${colors.background.paper};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  border-bottom: 1px solid ${colors.border.light};
`;

const Tr = styled.tr`
  &:hover {
    background: ${colors.background.paper};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  border-bottom: 1px solid ${colors.border.light};
`;

const BankInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const BankName = styled.span`
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BranchName = styled.span`
  font-size: 12px;
  color: ${colors.text.tertiary};
`;

const DefaultBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${borderRadius.sm};
  font-size: 10px;
  font-weight: 600;
  background: ${colors.status.warning}20;
  color: ${colors.status.warning};

  svg {
    font-size: 10px;
  }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    switch (props.$type) {
      case 'Current': return colors.primary.main + '20';
      case 'Savings': return colors.status.success + '20';
      case 'Fixed Deposit': return colors.secondary.main + '20';
      default: return colors.neutral[200];
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'Current': return colors.primary.main;
      case 'Savings': return colors.status.success;
      case 'Fixed Deposit': return colors.secondary.main;
      default: return colors.text.secondary;
    }
  }};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: ${borderRadius.xl};
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? colors.status.success + '20' : colors.neutral[200]};
  color: ${props => props.$active ? colors.status.success : colors.text.tertiary};
`;

const BalanceAmount = styled.span`
  font-weight: 600;
  color: ${props => props.$positive ? colors.status.success : colors.text.primary};
`;

const ActionDropdown = styled.div`
  position: fixed;
  min-width: 220px;
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.18);
  z-index: 9999;
  padding: 4px;
  overflow: hidden;
`;
const ActionDropItem = styled.button`
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none; border: none; text-align: left;
  font-size: 13px; cursor: pointer;
  color: ${p => p.$danger ? colors.error.main : colors.text.primary};
  border-radius: 6px;
  &:hover { background: ${p => p.$danger ? `${colors.error.main}10` : colors.neutral[50]}; }
  svg { flex-shrink: 0; opacity: 0.75; }
`;
const ActionDropDivider = styled.div`
  height: 1px; background: ${colors.neutral[200]}; margin: 4px 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${props => props.$danger ? colors.status.error + '10' : colors.background.paper};
  color: ${props => props.$danger ? colors.status.error : colors.text.secondary};
  border: none;
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$danger ? colors.status.error + '20' : colors.primary.main + '10'};
    color: ${props => props.$danger ? colors.status.error : colors.primary.main};
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: ${colors.text.secondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: ${colors.text.tertiary};

  svg {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    font-size: 16px;
    margin-bottom: 8px;
  }

  small {
    font-size: 13px;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.border.light};
`;

const ModalTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};

  svg {
    color: ${colors.primary.main};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: ${colors.text.tertiary};
  cursor: pointer;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.paper};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.border.light};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: ${props => props.$full ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}20;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: ${colors.text.secondary};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.background.paper};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default Banks;
